import {
  createContext, forwardRef, useContext, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import {
  X, Check, AlertCircle, Info, AlertTriangle, Loader2, Copy, ChevronDown, MoreVertical,
} from 'lucide-react';
import { cn } from '../../lib/cn';

/* ----------------------------------------------------------------- Button */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  loading?: boolean;
  icon?: ReactNode;
};

const BUTTON_VARIANTS = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 shadow-sm',
  secondary: 'surface app-border border text-app hover:bg-[rgb(var(--surface-raised))]',
  ghost: 'text-app hover:bg-[rgb(var(--surface-raised))]',
  subtle: 'bg-brand-50 text-brand-800 hover:bg-brand-100 dark:bg-brand-900/40 dark:text-brand-200 dark:hover:bg-brand-900/60',
  danger: 'bg-critical text-white hover:brightness-110 active:brightness-95 shadow-sm',
};

const BUTTON_SIZES = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9.5 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-base gap-2',
  icon: 'h-9 w-9 justify-center',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, className, children, disabled,
    type = 'button', ...props }, ref
) {
  return (
    <button
      ref={ref}
      // A bare <button> inside a <form> submits it. Every deliberate submit in
      // this app says so explicitly, so the safe default is the other one:
      // otherwise "Add a variable" in a settings dialog saves and closes it.
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center rounded-lg font-medium transition-colors select-none',
        'disabled:opacity-50 disabled:pointer-events-none',
        BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className
      )}
      {...props}
    >
      {loading ? <Loader2 size={16} className="animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

/* ------------------------------------------------------------------- Card */

export function Card({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('surface app-border border rounded-xl shadow-sm', className)} {...props}>
      {children}
    </div>
  );
}

export function CardHeader({ title, description, actions, className }: {
  title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-4 px-5 py-4 app-border border-b', className)}>
      <div className="min-w-0">
        <h2 className="font-semibold text-app truncate">{title}</h2>
        {description && <p className="text-sm text-muted mt-0.5">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export const CardBody = ({ className, children }: { className?: string; children: ReactNode }) => (
  <div className={cn('p-5', className)}>{children}</div>
);

/* ------------------------------------------------------------------ Field */

/** Lets a Field hand its generated id and description down to whichever
 *  control it wraps, so callers never have to invent matching id/htmlFor
 *  pairs by hand — and can never forget to. */
type FieldBinding = { id: string; describedBy?: string; invalid?: boolean };
const FieldContext = createContext<FieldBinding | null>(null);

/** Controls call this to adopt the surrounding Field's id, unless they were
 *  given an explicit one. */
function useFieldBinding(own: { id?: string; 'aria-describedby'?: string; invalid?: boolean }) {
  const field = useContext(FieldContext);
  if (!field) return { id: own.id, describedBy: own['aria-describedby'], invalid: own.invalid };
  return {
    id: own.id ?? field.id,
    describedBy: own['aria-describedby'] ?? field.describedBy,
    invalid: own.invalid ?? field.invalid,
  };
}

export function Field({ label, hint, error, required, children, htmlFor, group }: {
  label: ReactNode; hint?: ReactNode; error?: string | null;
  required?: boolean; children: ReactNode; htmlFor?: string;
  /** Set when the field wraps several controls (switches, a checkbox list)
   *  rather than one. A <label for> may only point at a single control, so
   *  the caption becomes a group name instead. */
  group?: boolean;
}) {
  const generatedId = useId();
  const id = htmlFor ?? generatedId;
  const labelId = `${id}-label`;
  const noteId = `${id}-note`;
  const note = error ?? hint;

  const caption = (
    <>
      {label}
      {required && <span className="text-critical ms-1" aria-hidden>*</span>}
    </>
  );
  const captionClass = 'block text-sm font-medium text-app';

  return (
    <div className="space-y-1.5" {...(group
      ? { role: 'group', 'aria-labelledby': labelId, 'aria-describedby': note ? noteId : undefined }
      : {})}
    >
      {group
        ? <span id={labelId} className={captionClass}>{caption}</span>
        : <label htmlFor={id} className={captionClass}>{caption}</label>}
      {group ? children : (
        <FieldContext.Provider
          value={{ id, describedBy: note ? noteId : undefined, invalid: Boolean(error) }}
        >
          {children}
        </FieldContext.Provider>
      )}
      {error
        ? <p id={noteId} className="text-xs text-critical flex items-center gap-1"><AlertCircle size={12} />{error}</p>
        : hint ? <p id={noteId} className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

const CONTROL = 'w-full rounded-lg app-border border surface px-3 py-2 text-sm text-app ' +
  'placeholder:text-[rgb(var(--text-muted))] focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 ' +
  'outline-none transition disabled:opacity-60';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ className, invalid, ...props }, ref) {
    const bound = useFieldBinding({ id: props.id, 'aria-describedby': props['aria-describedby'], invalid });
    return <input ref={ref} aria-invalid={bound.invalid || undefined}
      className={cn(CONTROL, bound.invalid && 'border-critical focus:border-critical focus:ring-critical/20', className)}
      {...props} id={bound.id} aria-describedby={bound.describedBy} />;
  }
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  function Textarea({ className, invalid, ...props }, ref) {
    const bound = useFieldBinding({ id: props.id, 'aria-describedby': props['aria-describedby'], invalid });
    return <textarea ref={ref} aria-invalid={bound.invalid || undefined}
      className={cn(CONTROL, 'min-h-24 resize-y', className)}
      {...props} id={bound.id} aria-describedby={bound.describedBy} />;
  }
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }>(
  function Select({ className, invalid, children, ...props }, ref) {
    const bound = useFieldBinding({ id: props.id, 'aria-describedby': props['aria-describedby'], invalid });
    return (
      <div className="relative">
        <select ref={ref} aria-invalid={bound.invalid || undefined}
          className={cn(CONTROL, 'appearance-none pe-9', className)}
          {...props} id={bound.id} aria-describedby={bound.describedBy}>{children}</select>
        <ChevronDown size={16} className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-muted" />
      </div>
    );
  }
);

export function Switch({ checked, onChange, label, disabled }: {
  checked: boolean; onChange: (value: boolean) => void; label?: string; disabled?: boolean;
}) {
  return (
    <button
      type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn('relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50',
        checked ? 'bg-brand-600' : 'bg-[rgb(var(--border))]')}
    >
      <span className={cn(
        'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all',
        // Uses inset-inline so the knob slides the right way in RTL.
        checked ? 'start-[1.375rem]' : 'start-0.5'
      )} />
    </button>
  );
}

/* ------------------------------------------------------------------ Badge */

const BADGE_TONES = {
  neutral: 'bg-[rgb(var(--surface-sunken))] text-muted app-border border',
  brand: 'bg-brand-50 text-brand-800 dark:bg-brand-900/40 dark:text-brand-200',
  critical: 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300',
  warning: 'bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
  info: 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
  ok: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
};

export type Tone = keyof typeof BADGE_TONES;

export function Badge({ tone = 'neutral', children, className, dot }: {
  tone?: Tone; children: ReactNode; className?: string; dot?: boolean;
}) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
      BADGE_TONES[tone], className)}>
      {dot && <span className={cn('h-1.5 w-1.5 rounded-full',
        tone === 'critical' ? 'bg-critical' : tone === 'warning' ? 'bg-warning'
        : tone === 'ok' ? 'bg-ok' : tone === 'info' ? 'bg-info' : 'bg-current')} />}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ Table */

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  // Wide tables scroll inside their own container rather than the page.
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn('w-full text-sm', className)}>{children}</table>
    </div>
  );
}

export const Th = ({ children, className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
  <th className={cn('text-start font-medium text-muted px-4 py-2.5 whitespace-nowrap', className)} {...props}>
    {children}
  </th>
);

export const Td = ({ children, className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) => (
  <td className={cn('px-4 py-2.5 text-app app-border border-t', className)} {...props}>{children}</td>
);

export const Tr = ({ children, className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) => (
  <tr className={cn('hover:bg-[rgb(var(--surface-raised))] transition-colors', className)} {...props}>
    {children}
  </tr>
);

/* ----------------------------------------------------------------- Dialog */

export function Dialog({ open, onClose, title, description, children, footer, size = 'md' }: {
  open: boolean; onClose: () => void; title: ReactNode; description?: ReactNode;
  children: ReactNode; footer?: ReactNode; size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Move focus into the dialog so keyboard users are not stranded behind it.
    //
    // A field first, and only then a button. The close button is the first
    // `button` in the markup, so matching "any control" put focus on ✕ every
    // time a dialog opened — and, because this fires 50ms late, it took focus
    // away from whatever somebody had already started typing into.
    const timer = setTimeout(() => {
      const dialog = ref.current;
      if (!dialog || dialog.contains(document.activeElement)) return;
      const field = dialog.querySelector<HTMLElement>(
        'input:not([type="hidden"]), select, textarea');
      (field ?? dialog.querySelector<HTMLElement>('button'))?.focus();
    }, 50);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(timer);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        ref={ref} role="dialog" aria-modal="true" aria-labelledby={titleId}
        className={cn('relative surface w-full rounded-t-2xl sm:rounded-2xl shadow-2xl app-border border',
          'max-h-[92vh] flex flex-col', widths[size])}
      >
        <div className="flex items-start justify-between gap-4 px-5 py-4 app-border border-b">
          <div className="min-w-0">
            <h2 id={titleId} className="font-semibold text-app">{title}</h2>
            {description && <p className="text-sm text-muted mt-0.5">{description}</p>}
          </div>
          <button onClick={onClose} aria-label="Close"
            className="rounded-lg p-1.5 text-muted hover:bg-[rgb(var(--surface-raised))] shrink-0">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto grow">{children}</div>
        {footer && <div className="px-5 py-4 app-border border-t flex justify-end gap-2 shrink-0">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

/* ------------------------------------------------------------------ Toast */

type Toast = { id: number; tone: Tone; title: string; body?: string };
const ToastContext = createContext<{ push: (t: Omit<Toast, 'id'>) => void } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = (toast: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { ...toast, id }]);
    // Errors linger; confirmations get out of the way.
    setTimeout(() => setToasts((c) => c.filter((t) => t.id !== id)),
      toast.tone === 'critical' ? 8000 : 4000);
  };

  const icons: Record<string, ReactNode> = {
    ok: <Check size={16} />, critical: <AlertCircle size={16} />,
    warning: <AlertTriangle size={16} />, info: <Info size={16} />,
    neutral: <Info size={16} />, brand: <Info size={16} />,
  };

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <div className="fixed bottom-4 end-4 z-[60] flex flex-col gap-2 w-[min(24rem,calc(100vw-2rem))]"
           role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id}
            className={cn('surface app-border border rounded-xl shadow-lg px-4 py-3 flex gap-3 items-start',
              toast.tone === 'critical' && 'border-critical/40')}>
            <span className={cn('mt-0.5 shrink-0',
              toast.tone === 'critical' ? 'text-critical' : toast.tone === 'ok' ? 'text-ok'
              : toast.tone === 'warning' ? 'text-warning' : 'text-info')}>
              {icons[toast.tone]}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-app">{toast.title}</p>
              {toast.body && <p className="text-xs text-muted mt-0.5 break-words">{toast.body}</p>}
            </div>
            <button onClick={() => setToasts((c) => c.filter((t) => t.id !== toast.id))}
              className="text-muted hover:text-app shrink-0" aria-label="Dismiss">
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return {
    success: (title: string, body?: string) => context.push({ tone: 'ok', title, body }),
    error: (title: string, body?: string) => context.push({ tone: 'critical', title, body }),
    warn: (title: string, body?: string) => context.push({ tone: 'warning', title, body }),
    info: (title: string, body?: string) => context.push({ tone: 'info', title, body }),
  };
}

/* ------------------------------------------------------------- misc bits */

export const Spinner = ({ className }: { className?: string }) => (
  <Loader2 className={cn('animate-spin text-muted', className)} size={18} aria-label="Loading" />
);

export const Skeleton = ({ className }: { className?: string }) => (
  <div className={cn('skeleton rounded-md', className)} aria-hidden />
);

export function EmptyState({ icon, title, description, action }: {
  icon?: ReactNode; title: string; description?: string; action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6">
      {icon && <div className="text-muted mb-3 opacity-60">{icon}</div>}
      {/* A heading, not a paragraph: this is often the only thing on the
          screen, and screen-reader users navigate by heading. */}
      <h3 className="font-medium text-app">{title}</h3>
      {description && <p className="text-sm text-muted mt-1 max-w-md">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function CopyButton({ value, label, className }: { value: string; label?: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button" size="sm" variant="ghost" className={className}
      icon={copied ? <Check size={14} className="text-ok" /> : <Copy size={14} />}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
        } catch {
          // Clipboard API needs a secure context; fall back so http://<lan-ip> works.
          const area = document.createElement('textarea');
          area.value = value;
          area.style.position = 'fixed';
          area.style.opacity = '0';
          document.body.appendChild(area);
          area.select();
          document.execCommand('copy');
          area.remove();
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
    >
      {copied ? 'Copied' : label ?? 'Copy'}
    </Button>
  );
}

export function Tabs({ tabs, active, onChange }: {
  tabs: Array<{ id: string; label: ReactNode; count?: number }>;
  active: string; onChange: (id: string) => void;
}) {
  return (
    <div className="flex gap-1 app-border border-b overflow-x-auto" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id} type="button" role="tab" aria-selected={active === tab.id}
          onClick={() => onChange(tab.id)}
          className={cn(
            'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
            active === tab.id
              ? 'border-brand-600 text-brand-700 dark:text-brand-300'
              : 'border-transparent text-muted hover:text-app'
          )}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span className="ms-2 text-xs opacity-70">{tab.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/** Error boundary content: never a blank screen. */
export function ErrorPanel({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : String(error ?? 'Something went wrong');
  return (
    <Card className="border-critical/30">
      <CardBody className="flex items-start gap-3">
        <AlertCircle className="text-critical shrink-0 mt-0.5" size={18} />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-app">Could not load this</p>
          <p className="text-sm text-muted mt-1 break-words">{message}</p>
        </div>
        {onRetry && <Button size="sm" onClick={onRetry}>Retry</Button>}
      </CardBody>
    </Card>
  );
}

/* ------------------------------------------------------------------- Menu */

/**
 * An actions popover for a card or a row.
 *
 * Closes on Escape, on an outside click, and on choosing an item — a menu that
 * stays open after "Delete…" invites a second, accidental click on whatever
 * has moved under the cursor.
 */
export function Menu({ label = 'Actions', align = 'end', children }: {
  label?: string; align?: 'start' | 'end'; children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <Button
        type="button" size="icon" variant="ghost" aria-label={label}
        aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <MoreVertical size={16} />
      </Button>
      {open && (
        <div
          role="menu" aria-label={label} onClick={() => setOpen(false)}
          className={cn(
            'absolute top-full mt-1 z-30 min-w-48 surface app-border border rounded-lg shadow-lg p-1',
            align === 'end' ? 'end-0' : 'start-0'
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function MenuItem({ icon, tone = 'default', className, children, ...props }:
  ButtonHTMLAttributes<HTMLButtonElement> & { icon?: ReactNode; tone?: 'default' | 'critical' }) {
  return (
    <button
      type="button" role="menuitem"
      className={cn(
        'w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-start',
        'disabled:opacity-50 disabled:pointer-events-none',
        tone === 'critical'
          ? 'text-critical hover:bg-red-50 dark:hover:bg-red-950/30'
          : 'text-app hover:bg-[rgb(var(--surface-raised))]',
        className
      )}
      {...props}
    >
      {icon}
      {children}
    </button>
  );
}

export const MenuSeparator = () => (
  <div role="separator" className="my-1 h-px bg-[rgb(var(--border))]" />
);

/* ---------------------------------------------------------- ConfirmDialog */

/**
 * A yes/no dialog for something Cancel cannot undo afterwards.
 *
 * `error` renders inside the dialog rather than as a toast, because the answer
 * to "3 share links point at this" is a second decision taken right here — not
 * a message that slides away after four seconds.
 */
export function ConfirmDialog({
  open, onClose, onConfirm, title, description, confirmLabel = 'Confirm',
  tone = 'critical', loading, error, children,
}: {
  open: boolean; onClose: () => void; onConfirm: () => void;
  title: ReactNode; description?: ReactNode; confirmLabel?: ReactNode;
  tone?: 'critical' | 'brand'; loading?: boolean; error?: ReactNode; children?: ReactNode;
}) {
  return (
    <Dialog
      open={open} onClose={onClose} title={title} description={description} size="sm"
      footer={<>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant={tone === 'critical' ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </>}
    >
      <div className="space-y-3">
        {children}
        {error && (
          <div className="flex items-start gap-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 text-sm text-amber-900 dark:text-amber-200">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div className="min-w-0">{error}</div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
